import { useEffect, useRef } from "react";
import DPDPphoto from "./assets/DPDPphoto.jpg";

const PILLARS = [
  {
    title: "DPDP Act 2023 Compliant",
    desc: "Full alignment with India's Digital Personal Data Protection Act. Consent frameworks, data processing agreements, and notification protocols built in — not bolted on.",
    tag: "Legal & Regulatory",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2bb3a3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
  {
    title: "End-to-End Encrypted Storage",
    desc: "No raw footage. No biometric identifiers retained in the open. Processed metrics are encrypted at rest and in transit — only you hold the keys.",
    tag:"Privacy by Design",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
  },
  {
    title: "Institution Data Sovereignty",
    desc: "Your institution owns its data, full stop. We never share, sell, or access it without explicit authorisation. On-premise deployment available for maximum control.",
    tag: "Data Ownership",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2bb3a3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      </svg>
    ),
  },
  {
    title: "Guaranteed Opt-Out",
    desc: "Every individual has the right to opt out. We mandate this mechanism before any deployment. Opted-out individuals are excluded at the system level — automatically.",
    tag: "Individual Rights",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 11 12 14 22 4" />
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
    ),
  },
];

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

  .tc-section {
    font-family: 'DM Sans', sans-serif;
    background: #f8faff;
    width: 100%;
    padding: 80px 40px;
    overflow: hidden;
    position: relative;
  }

  /* Bg orbs */
  .tc-orb {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
    filter: blur(70px);
  }
  .tc-orb-1 {
    width: 520px; height: 520px;
    background: radial-gradient(circle, rgba(43,179,163,0.1) 0%, transparent 70%);
    top: -120px; right: 40px;
    animation: tcOrbDrift 10s ease-in-out infinite;
  }
  .tc-orb-2 {
    width: 320px; height: 320px;
    background: radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%);
    bottom: -60px; left: 20px;
    animation: tcOrbDrift 14s ease-in-out infinite reverse;
  }
  @keyframes tcOrbDrift {
    0%,100% { transform: translate(0,0) scale(1); }
    50%      { transform: translate(-20px,18px) scale(1.04); }
  }

  /* Grid layout */
  .tc-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 48px;
    align-items: start;
    max-width: 100%;
    margin: 0 auto;
    position: relative;
    z-index: 2;
  }

  /* ── LEFT IMAGE ── */
  .tc-left {
    opacity: 0;
    transform: translateX(-28px);
    animation: tcFadeR 0.9s ease forwards 0.15s;
  }
  @keyframes tcFadeR { to { opacity: 1; transform: translateX(0); } }

  .tc-img-wrap { position: relative; }

  .tc-img-ring {
    position: absolute;
    inset: -3px;
    border-radius: 26px;
    background: linear-gradient(135deg, #2bb3a3, #6366f1, #a78bfa, #2bb3a3);
    background-size: 300% 300%;
    animation: tcBorderRot 5s linear infinite;
    z-index: 0;
  }
  @keyframes tcBorderRot {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  .tc-img-inner {
    position: relative;
    z-index: 1;
    border-radius: 23px;
    overflow: hidden;
    border: 3px solid #f8faff;
  }
  .tc-img-inner img {
    width: 100%;
    height: 600px;
    object-fit: contain;
    background: #0a0f1e;
    display: block;
    filter: saturate(1.1) brightness(0.88);
    transition: transform 0.7s ease;
  }
  .tc-img-inner:hover img { transform: scale(1.04); }

  .tc-img-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 45%, rgba(10,15,30,0.84) 100%);
    z-index: 2;
    border-radius: 21px;
  }

  /* Scan line */
  .tc-scan {
    position: absolute;
    left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(43,179,163,0.9), transparent);
    z-index: 3;
    animation: tcScanPulse 3.5s ease-in-out infinite;
    top: 30%;
  }
  @keyframes tcScanPulse {
    0%,100% { opacity: 0.4; transform: scaleX(0.8); }
    50%      { opacity: 1;   transform: scaleX(1); }
  }

  /* HUD corners */
  .tc-cor { position: absolute; width: 22px; height: 22px; z-index: 4; }
  .tc-cor::before, .tc-cor::after { content: ''; position: absolute; background: #2bb3a3; }
  .tc-ctl { top: 12px; left: 12px; }
  .tc-ctl::before { top: 0; left: 0; width: 2px; height: 100%; }
  .tc-ctl::after  { top: 0; left: 0; width: 100%; height: 2px; }
  .tc-ctr { top: 12px; right: 12px; }
  .tc-ctr::before { top: 0; right: 0; width: 2px; height: 100%; }
  .tc-ctr::after  { top: 0; right: 0; width: 100%; height: 2px; }
  .tc-cbl { bottom: 12px; left: 12px; }
  .tc-cbl::before { bottom: 0; left: 0; width: 2px; height: 100%; }
  .tc-cbl::after  { bottom: 0; left: 0; width: 100%; height: 2px; }
  .tc-cbr { bottom: 12px; right: 12px; }
  .tc-cbr::before { bottom: 0; right: 0; width: 2px; height: 100%; }
  .tc-cbr::after  { bottom: 0; right: 0; width: 100%; height: 2px; }

  /* Floating badges */
  .tc-badge {
    position: absolute;
    z-index: 5;
    background: rgba(255,255,255,0.97);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    border-radius: 14px;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    gap: 12px;
    border: 1px solid rgba(43,179,163,0.25);
    box-shadow: 0 8px 32px rgba(43,179,163,0.16);
  }
  .tc-badge-bl {
    bottom: 20px; left: 20px;
    animation: tcFloat 4.5s ease-in-out infinite;
  }
  .tc-badge-tr {
    top: 20px; right: 20px;
    background: rgba(10,15,30,0.88);
    border-color: rgba(43,179,163,0.3);
    animation: tcFloat 5s ease-in-out infinite;
    animation-delay: -2.2s;
  }
  @keyframes tcFloat {
    0%,100% { transform: translateY(0); }
    50%      { transform: translateY(-8px); }
  }

  .tc-live-dot {
    width: 10px; height: 10px;
    border-radius: 50%; background: #2bb3a3;
    flex-shrink: 0; position: relative;
    animation: tcDotBlink 2s ease-in-out infinite;
  }
  .tc-live-dot::after {
    content: '';
    position: absolute; inset: -4px;
    border-radius: 50%;
    border: 1.5px solid rgba(43,179,163,0.4);
    animation: tcRingOut 2s ease-out infinite;
  }
  @keyframes tcDotBlink { 0%,100%{opacity:1} 50%{opacity:0.3} }
  @keyframes tcRingOut  { 0%{transform:scale(1);opacity:0.5} 100%{transform:scale(2.4);opacity:0} }

  .tc-bval  { font-family:'DM Sans',sans-serif; font-size:16px; font-weight:800; color:#0f172a; line-height:1; }
  .tc-blbl  { font-size:11px; color:#64748b; text-transform:uppercase; letter-spacing:0.1em; margin-top:2px; }
  .tc-bval2 { font-family:'DM Sans',sans-serif; font-size:14px; font-weight:700; color:#2bb3a3; line-height:1; }
  .tc-blbl2 { font-size:10px; color:rgba(255,255,255,0.45); text-transform:uppercase; letter-spacing:0.1em; margin-top:3px; }

  /* Stat mini cards */
  .tc-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 20px;
  }
  .tc-stat-card {
    background: white;
    border: 1.5px solid rgba(43,179,163,0.15);
    border-radius: 14px;
    padding: 12px 16px;
    opacity: 0;
    animation: tcCountUp 0.6s ease both;
  }
  .tc-stat-card:nth-child(1) { animation-delay: 1.0s; }
  .tc-stat-card:nth-child(2) { animation-delay: 1.15s; }
  @keyframes tcCountUp {
    from { opacity:0; transform:translateY(10px); }
    to   { opacity:1; transform:translateY(0); }
  }
  .tc-stat-num {
    font-family: 'DM Sans', sans-serif;
    font-size: 26px; font-weight: 800; line-height: 1;
    background: linear-gradient(90deg,#2bb3a3,#6366f1);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .tc-stat-lbl {
    font-size: 11px; color: #94a3b8;
    text-transform: uppercase; letter-spacing: 0.1em;
    margin-top: 4px;
  }

  /* ── RIGHT CONTENT ── */
  .tc-right {
    opacity: 0;
    transform: translateX(28px);
    animation: tcFadeL 0.9s ease forwards 0.3s;
  }
  @keyframes tcFadeL { to { opacity: 1; transform: translateX(0); } }

  .tc-label {
    font-family: 'DM Sans', sans-serif;
    font-size: 10px; font-weight: 700;
    letter-spacing: 0.22em; text-transform: uppercase;
    color: #2bb3a3; margin-bottom: 16px;
    display: flex; align-items: center; gap: 10px;
  }
  .tc-label::before {
    content: '';
    width: 28px; height: 1px;
    background: linear-gradient(90deg, #2bb3a3, #6366f1);
  }

  .tc-heading {
    font-family: 'DM Sans', sans-serif;
    font-size: clamp(32px, 3vw, 44px);
    font-weight: 800; line-height: 1.05;
    color: #0f172a; margin-bottom: 20px;
  }
  .tc-heading .gr {
    background: linear-gradient(90deg, #2bb3a3, #6366f1);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .tc-body {
    font-size: 25px; color: #64748b;
    line-height: 1.8; margin-bottom: 8px;
  }
  .tc-body strong { color: #2bb3a3; font-weight: 600; }

  .tc-shimbar {
    height: 2px; margin: 28px 0;
    background: linear-gradient(90deg, transparent, rgba(43,179,163,0.5), rgba(99,102,241,0.5), transparent);
    background-size: 200% auto;
    animation: tcShimLine 3s linear infinite;
    border-radius: 2px;
  }
  @keyframes tcShimLine {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }

  /* Pillars */
  .tc-pillars { display: flex; flex-direction: column; gap: 8px; }

  .tc-pill {
    display: flex; gap: 16px;
    padding: 12px 16px;
    border-radius: 16px;
    border: 1.5px solid rgba(43,179,163,0.12);
    background: white;
    transition: all 0.35s cubic-bezier(0.34,1.56,0.64,1);
    cursor: default;
    opacity: 0;
    position: relative;
    overflow: hidden;
  }
  .tc-pill::before {
    content: '';
    position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
    background: linear-gradient(180deg, #2bb3a3, #6366f1);
    transform: scaleY(0); transform-origin: bottom;
    transition: transform 0.4s ease;
    border-radius: 0 2px 2px 0;
  }
  .tc-pill:hover {
    border-color: rgba(43,179,163,0.35);
    box-shadow: 0 12px 36px rgba(43,179,163,0.14);
    transform: translateX(6px) scale(1.01);
  }
  .tc-pill:hover::before { transform: scaleY(1); }

  .tc-pill.tc-vis {
    opacity: 1;
    animation: tcCardSlide 0.6s ease both;
  }
  .tc-pill:nth-child(1) { animation-delay: 0.55s; }
  .tc-pill:nth-child(2) { animation-delay: 0.7s;  }
  .tc-pill:nth-child(3) { animation-delay: 0.85s; }
  .tc-pill:nth-child(4) { animation-delay: 1.0s;  }
  @keyframes tcCardSlide {
    from { opacity:0; transform:translateX(28px); }
    to   { opacity:1; transform:translateX(0); }
  }

  .tc-picon {
    width: 44px; height: 44px;
    border-radius: 12px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: linear-gradient(135deg, rgba(43,179,163,0.1), rgba(99,102,241,0.07));
    transition: transform 0.35s ease;
  }
  .tc-pill:hover .tc-picon { transform: scale(1.15) rotate(-5deg); }

  .tc-ptitle {
    font-family: 'DM Sans', sans-serif;
    font-size: 20px; font-weight: 700;
    color: #0f172a; margin-bottom: 4px; line-height: 1.2;
  }
  .tc-pdesc { font-size: 17px; color: #64748b; line-height: 1.65; }

  .tc-ptag {
    display: inline-block; margin-top: 6px;
    padding: 3px 10px; border-radius: 100px;
    font-size: 12px; font-weight: 600; letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(43,179,163,0.08); color: #2bb3a3;
    border: 1px solid rgba(43,179,163,0.2);
    opacity: 0; transform: translateY(4px);
    transition: opacity 0.3s ease 0.05s, transform 0.3s ease 0.05s;
  }
  .tc-pill:hover .tc-ptag { opacity: 1; transform: translateY(0); }

  @media (max-width: 1024px) {
    .tc-grid { grid-template-columns: 1fr; gap: 40px; }
    .tc-section { padding: 80px 28px; }
    .tc-img-inner img {
    height: auto;
    max-height: 420px;
    object-fit: contain;
}
  }
`;

export default function TrustComplianceSection() {
  const pillarsRef = useRef(null);

  useEffect(() => {
    if (!pillarsRef.current) return;
    const pills = pillarsRef.current.querySelectorAll(".tc-pill");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("tc-vis");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    pills.forEach((p) => obs.observe(p));
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <style>{css}</style>
      <section id="trust" className="tc-section">
        <div className="tc-orb tc-orb-1" />
        <div className="tc-orb tc-orb-2" />

        <div className="tc-grid">
          {/* ── LEFT ── */}
          <div className="tc-left">
            <div className="tc-img-wrap">
              <div className="tc-img-ring" />
              
               
           

              <div className="tc-img-inner">
                <img src={DPDPphoto} alt="DPDP Act 2023 Compliance" />
                <div className="tc-img-overlay" />
              </div>

               

              {/* Top-right badge */}
              <div className="tc-badge tc-badge-tr">
                <div>
                  <div className="tc-bval2">100% Private</div>
                  <div className="tc-blbl2">Zero raw storage</div>
                </div>
              </div>
            </div>

            {/* Stat cards below image */}
            <div className="tc-stats">
              <div className="tc-stat-card">
                <div className="tc-stat-num">Secure</div>
                <div className="tc-stat-lbl">Profile data management</div>
              </div>
              <div className="tc-stat-card">
                <div className="tc-stat-num">Compliant</div>
                <div className="tc-stat-lbl">Opt-out Controls</div>
              </div>
            </div>
          </div>

          {/* ── RIGHT ── */}
          <div className="tc-right">
            <div className="tc-label">Trust &amp; Compliance</div>
            <h2 className="tc-heading">
              Built on Ethics.<br />
              <span className="gr">Grounded in Law.</span>
            </h2>
            <p className="tc-body">
              Sentio Mind was designed from day one to operate within{" "}
              <strong>India's regulatory framework</strong> — not retrofitted for compliance.
              Every architectural decision reflects both legal obligations and ethical
              responsibility.
            </p>

            <div className="tc-shimbar" />

            <div className="tc-pillars" ref={pillarsRef}>
              {PILLARS.map((p, i) => (
                <div key={i} className="tc-pill">
                  <div className="tc-picon">{p.icon}</div>
                  <div>
                    <div className="tc-ptitle">{p.title}</div>
                    <div className="tc-pdesc">{p.desc}</div>
                    <span className="tc-ptag">{p.tag}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}